import React, { createContext, useEffect, useState } from "react";
import { Entity, Intermediary, SalesPerson, SelectedEntity } from "view360Card/models";
import { SearchEntityTypeEnum } from "view360Card/models/SearchEntityTypeEnum";
import { SearchResults } from "view360Card/models/SearchResults";
import view360Service from "view360Card/service";
import { IntermediaryFromResponse } from "./IntermediaryFromResponse";
import { SalesPersonFromResponse } from "./SalesPersonFromResponse";
import { BrowserHistory } from "history";
import { constants } from "shared/constants";
import ErrorOr from "shared/ErrorOr";
import { DictionaryItemFromResponse } from "./DictionaryItemFromResponse";

type IntermediaryOrSalesPerson = Intermediary | SalesPerson;

export interface View360StoreProps {
    history: BrowserHistory;
}

export interface View360ContextProps {
    isSearchInProgress: boolean;
    isSearchError: boolean;
    searchPhrase: string | undefined;
    searchResults: SearchResults | undefined;
    selectedEntity: SelectedEntity | undefined;
    selectEntityToPresent: (entity: SelectedEntity) => void;
    selectedEntityDetails: Intermediary | SalesPerson | Entity | undefined;
    loadEntityDetails: () => Promise<ErrorOr<IntermediaryOrSalesPerson>>;
    runQuery: (phrase: string, limit: number) => Promise<void>;
    resetSearch: () => void;
    pushNewGeneralInfoViewUrl: (url: string) => void;
    popLastGeneralInfoViewUrl: () => string | null;
    reset360ViewHistory: () => void;
    cardHeaderSubtitle: string | undefined;
    setCardHeaderSubtitle: (subtitle: string | undefined) => void;
}

export const defaultView360ContextProps: View360ContextProps = {
    isSearchInProgress: false,
    isSearchError: false,
    searchPhrase: undefined,
    searchResults: undefined,
    selectedEntity: undefined,
    selectedEntityDetails: undefined,
    selectEntityToPresent: (entity: SelectedEntity) => null,
    loadEntityDetails: () => new Promise<ErrorOr<IntermediaryOrSalesPerson>>((_) => null),
    runQuery: (phrase: string, limit: number) => new Promise<void>((_) => null),
    resetSearch: () => null,
    pushNewGeneralInfoViewUrl: () => null,
    popLastGeneralInfoViewUrl: () => null,
    reset360ViewHistory: () => null,
    cardHeaderSubtitle: undefined,
    setCardHeaderSubtitle: (subtitle: string | undefined) => null,
};

export const View360Context = createContext(defaultView360ContextProps);

const View360Store: React.FC<View360StoreProps> = (props) => {
    const [searchPhrase, setSearchPhrase] = useState<string | undefined>();
    const [isSearchInProgress, setIsSearchInProgress] = useState(false);
    const [isSearchError, setIsSearchError] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResults | undefined>();
    const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | undefined>();
    const [selectedEntityDetails, setSelectedEntityDetails] = useState<Intermediary | SalesPerson>();
    const [previousGeneralInfoViewUrls, setPreviousGeneralInfoViewUrls] = useState<string[]>([]);
    const [cardHeaderSubtitle, setCardHeaderSubtitle] = useState<string>();

    useEffect(() => {
        props.history.listen((listerner) => {
            const pathName = listerner.location.pathname;
            if (pathName.endsWith(constants.defaultNavigateBackView360Tab)) {
                pushNewGeneralInfoViewUrl(listerner.location.pathname);
            }
        });
    }, []);

    const runQuery = async (phrase: string, limit: number) => {
        setIsSearchInProgress(true);
        setSearchPhrase(phrase);
        setSearchResults(undefined);
        setIsSearchError(false);
        try {
            const result = await view360Service.searchEntities(phrase, limit);
            setSearchResults({
                hasMore: result.hasMore,
                entities: result.list?.map((entity) => {
                    return {
                        entityId: entity.entityId,
                        entityType:
                            entity.entityType === "INTERMEDIARY"
                                ? SearchEntityTypeEnum.Intermediary
                                : entity.entityType === "OFWCA"
                                ? SearchEntityTypeEnum.SalesPerson
                                : SearchEntityTypeEnum.Unknown,
                        intermediaryList: entity.intermediaryList,
                        status: new DictionaryItemFromResponse(entity.status).domainModel(),
                        name: entity.name,
                    };
                }),
            });
        } catch (error) {
            setIsSearchError(true);
        } finally {
            setIsSearchInProgress(false);
        }
    };

    const selectEntityToPresent = (entity: SelectedEntity) => {
        if (selectedEntity?.entityId !== entity.entityId || selectedEntity?.entityType !== entity.entityType) {
            setSelectedEntityDetails(undefined);
            setSelectedEntity(entity);
        }
    };

    const resetSearch = () => {
        setSelectedEntity(undefined);
        setSelectedEntityDetails(undefined);
    };

    const loadEntityDetails = async () => {
        setSelectedEntityDetails(undefined);

        if (!selectedEntity) {
            return ErrorOr.Failed<Intermediary | SalesPerson>(new Error("Brak wybranego elementu"));
        }

        if (selectedEntity.entityType === SearchEntityTypeEnum.Intermediary && selectedEntity?.entityId) {
            const dto = await view360Service.getItermediaryDetails(selectedEntity.entityId);
            if (dto.isFailure) {
                return ErrorOr.Failed<IntermediaryOrSalesPerson>(dto.exception!);
            }
            const model = new IntermediaryFromResponse(dto.data!).domainModel();
            setSelectedEntityDetails(model);
            return ErrorOr.Success<IntermediaryOrSalesPerson>(model);
        }

        if (selectedEntity.entityType === SearchEntityTypeEnum.SalesPerson && selectedEntity?.entityId) {
            const dto = await view360Service.getSalesPersonDetails(selectedEntity.entityId);
            if (dto.isFailure) {
                return ErrorOr.Failed<IntermediaryOrSalesPerson>(dto.exception!);
            }
            const model = new SalesPersonFromResponse(dto.data!).salesPerson();
            setSelectedEntityDetails(model);
            return ErrorOr.Success<IntermediaryOrSalesPerson>(model);
        }

        return ErrorOr.Failed<IntermediaryOrSalesPerson>(new Error("Nieobsłużony scenariusz"));
    };

    const pushNewGeneralInfoViewUrl = (url: string) => {
        if (!url) {
            throw new Error("Url nie może być pusty");
        }

        if (previousGeneralInfoViewUrls.length === 0) {
            previousGeneralInfoViewUrls.push(url);
        }

        if (previousGeneralInfoViewUrls[previousGeneralInfoViewUrls.length - 1] !== url) {
            previousGeneralInfoViewUrls.push(url);
        }

        return;
    };

    const popLastGeneralInfoViewUrl = () => {
        if (previousGeneralInfoViewUrls.length === 0) {
            return null;
        }
        previousGeneralInfoViewUrls.pop();
        const previousUrl = previousGeneralInfoViewUrls.pop();
        if (!previousUrl) {
            return null;
        }

        return previousUrl;
    };

    const reset360ViewHistory = () => {
        previousGeneralInfoViewUrls.length = 0;
    };

    return (
        <View360Context.Provider
            value={{
                isSearchInProgress: isSearchInProgress,
                isSearchError: isSearchError,
                searchPhrase: searchPhrase,
                searchResults: searchResults,
                selectedEntity: selectedEntity,
                selectedEntityDetails: selectedEntityDetails,
                loadEntityDetails: loadEntityDetails,
                selectEntityToPresent: selectEntityToPresent,
                runQuery: runQuery,
                resetSearch: resetSearch,
                pushNewGeneralInfoViewUrl: pushNewGeneralInfoViewUrl,
                popLastGeneralInfoViewUrl: popLastGeneralInfoViewUrl,
                reset360ViewHistory: reset360ViewHistory,
                cardHeaderSubtitle: cardHeaderSubtitle,
                setCardHeaderSubtitle: setCardHeaderSubtitle,
            }}
        >
            {props.children}
        </View360Context.Provider>
    );
};

export default View360Store;
